import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
    Output,
    ViewChild,
    AfterViewInit,
} from '@angular/core';
import { Customer } from '@klickdata/core/customer';
import { AppScope } from '@klickdata/core/resource';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    TableFilterComponent,
} from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';

@Component({
    selector: 'app-custom-tabs-editor-filters',
    templateUrl: './custom-tabs-editor-filters.component.html',
    styleUrls: ['./custom-tabs-editor-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomTabsEditorFiltersComponent implements AfterViewInit {
    @Input() public customer: Customer;
    @Input() public appliedFilters: Filter<string | number>[] = [];
    @Input() public disabled: boolean = false;
    @ViewChild('filter') filter: TableFilterComponent;
    AppScope = AppScope;
    GlobalFilterProperty = GlobalFilterProperty;
    @Output() appliedFiltersChange: EventEmitter<Filter<string | number>[]> = new EventEmitter<
        Filter<string | number>[]
    >();
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public resourceSelectOptions: SelectFilterOption[];
    public permissionTypeOptions: SelectFilterOption[];
    public accessRoleOptions: SelectFilterOption[];
    public resourceTypeOptions: SelectFilterOption[];
    public dashboardTabsTypeOptions: SelectFilterOption[];

    constructor(@Inject(LOCALE_ID) public localeId: string, protected cdr: ChangeDetectorRef) {
        this.localeId = this.localeId === 'en' ? 'en-US' : localeId;
        this.dashboardTabsTypeOptions = [
            { title: $localize`Activity`, value: 'userActivity', class: 'base-filter-option' },
            { title: $localize`Assigned`, value: 'assigned', class: 'base-filter-option' },
            { title: $localize`Ongoing`, value: 'started', class: 'base-filter-option' },
            { title: $localize`Done`, value: 'done', class: 'base-filter-option' },
            { title: $localize`Favorite`, value: 'favorite', class: 'base-filter-option' },
            { title: $localize`Recommended`, value: 'recommended', class: 'base-filter-option' },
            { title: $localize`Signed Off`, value: 'signedoff', class: 'base-filter-option' },
            { title: $localize`Mandatory`, value: 'mandatory', class: 'base-filter-option' },
            { title: $localize`Enrolled`, value: 'enrolled', class: 'base-filter-option' },
            { title: $localize`Occasion Activity`, value: 'occasionActivity', class: 'base-filter-option' },
            { title: $localize`Application Activity`, value: 'applicationActivity', class: 'base-filter-option' },
            { title: $localize`AI Prompts`, value: 'prompts', class: 'base-filter-option' },
            { title: $localize`Practice`, value: 'practiceForUser', class: 'base-filter-option' },
        ];

        this.resourceSelectOptions = [
            { title: $localize`Public`, value: 'public', icon: 'public' },
            { title: $localize`None Public`, value: 'not_public', icon: 'public_off' },
            {
                title: $localize`Published`,
                value: 'published',
                icon: 'published_with_changes',
                class: 'material-icons-outlined',
            },
            {
                title: $localize`Not on Publish`,
                value: 'not_last_published',
                icon: 'unpublished',
                class: 'material-icons-outlined',
            },
            { title: $localize`Not Published yet`, value: 'not_published', icon: 'unpublished' },
            { title: $localize`Deleted`, value: 'deleted', icon: 'auto_delete' },
            { title: $localize`Expired`, value: 'expired', icon: 'event_busy' },
        ];
        this.permissionTypeOptions = [
            {
                value: 'enroll_approval_required',
                title: $localize`Approval for enroll`,
                icon: 'app_registration',
            },
            {
                value: 'mandatory',
                title: $localize`Mandatory`,
                icon: 'report',
            },
            {
                value: 'view',
                title: $localize`Visibility`,
                icon: 'preview',
            },
            {
                value: 'signoff_required',
                title: $localize`Approval /Sign off`,
                icon: 'assignment_turned_in',
            },
        ];
        this.accessRoleOptions = [
            {
                value: 'owned',
                title: $localize`Author only`,
                icon: 'attribution',
                class: 'access-role-owned',
            },
            {
                value: 'owned_with_editors',
                title: $localize`Author with editors`,
                icon: 'rate_review',
                class: 'access-role-owned-with-editors',
            },
            {
                value: 'editor',
                title: $localize`Editor`,
                icon: 'border_color',
                class: 'access-role-editor',
            },
            {
                value: 'acquired',
                title: $localize`Acquired`,
                icon: 'handshake',
                class: 'access-role-acquired',
            },
        ];
        this.resourceTypeOptions = Utils.getResourceTypeOptions();
    }
    onFilterChange(filters: Filter<string | number>[]) {
        this.appliedFiltersChange.emit(filters.filter((filter) => !!filter?.items?.length));
    }
    ngAfterViewInit(): void {
        if (!!this.appliedFilters?.length && this.filter) {
            // Group date filters (begin/end) together
            const processedFilters: Filter<string | number>[] = [];
            const dateFilterParts = { begin: null, end: null };

            this.appliedFilters.forEach((filter) => {
                // Check if this is a date filter composite part
                if (filter.property === 'begin' || filter.property === 'end') {
                    dateFilterParts[filter.property] = filter;
                } else {
                    processedFilters.push(filter);
                }
            });

            // If we have date filter parts, combine them into a single date filter
            if (dateFilterParts.begin || dateFilterParts.end) {
                const beginItems = dateFilterParts.begin?.items || [];
                const endItems = dateFilterParts.end?.items || [];

                // Create a composite date filter with both begin and end
                const dateFilter: Filter<string | number> = {
                    property: GlobalFilterProperty.DATE,
                    items: [...beginItems, ...endItems],
                    composite: [
                        { property: 'begin', items: beginItems },
                        { property: 'end', items: endItems },
                    ],
                };

                processedFilters.push(dateFilter);
            }

            // Apply all processed filters
            processedFilters.forEach((filter) => {
                this.filter.filterByModel({ property: filter.property, items: filter.items }, false, false);
            });

            this.cdr.markForCheck();
        }
    }
}
